import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Tag,
  Popconfirm,
  message,
  Card,
  Avatar,
  Space,
  Empty,
  Badge,
} from "antd";
import {
  LockOutlined,
  ReloadOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import UserAPI from "../../utils/api/UserAPI";
import {
  BRAND,
  primaryBtn,
  ghostBtn,
  injectButtonHoverStyles,
} from "./buttonStyles";

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).replace(/@.*/, "").split(/[.\s_]+/).filter(Boolean);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

const fullName = (row) => {
  const n = [row.firstname, row.lastname].filter(Boolean).join(" ").trim();
  return n || row.username || "-";
};

const LockedAccountsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await UserAPI.getLockedAccounts();
      if (res && res.data && res.data.status) {
        setData(res.data.data || []);
      } else {
        message.error((res && res.data && res.data.message) || "Failed to load");
      }
    } catch (e) {
      message.error("Unable to fetch locked accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    injectButtonHoverStyles();
    fetch();
  }, []);

  const handleRestore = async (userId) => {
    if (!userId) return;
    setRestoringId(userId);
    try {
      const res = await UserAPI.unlockAccount(userId);
      if (res && res.data && res.data.status) {
        message.success("Account restored");
        fetch();
      } else {
        message.error((res && res.data && res.data.message) || "Failed to restore");
      }
    } catch (e) {
      message.error("Failed to restore account");
    } finally {
      setRestoringId(null);
    }
  };

  const columns = [
    {
      title: "User",
      dataIndex: "username",
      key: "username",
      width: 300,
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Badge count={<LockOutlined style={{ color: "#ff4d4f" }} />} offset={[-2, 32]}>
            <Avatar
              size={36}
              style={{
                background: BRAND,
                fontSize: 13,
                textTransform: "uppercase",
                flexShrink: 0,
              }}
            >
              {initials(row.username)}
            </Avatar>
          </Badge>
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{fullName(row)}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c", wordBreak: "break-all" }}>
              {row.username}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Roles",
      dataIndex: "role",
      key: "role",
      width: 200,
      render: (v) =>
        Array.isArray(v) && v.length ? (
          <Space size={[4, 4]} wrap>
            {v.map((r) => (
              <Tag
                key={r}
                style={{
                  borderRadius: 10,
                  color: BRAND,
                  background: "#66339914",
                  border: "1px solid #66339933",
                  marginInlineEnd: 0,
                }}
              >
                {r}
              </Tag>
            ))}
          </Space>
        ) : (
          "-"
        ),
    },
    {
      title: "Blocked at",
      key: "blocked_at",
      render: (_, row) => {
        const v = row.security && row.security.blocked_at;
        return v ? (
          <div>
            <div style={{ fontWeight: 500 }}>{new Date(v).toLocaleDateString()}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {new Date(v).toLocaleTimeString()}
            </div>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Reason",
      key: "reason",
      render: (_, row) => {
        const r = row.security && row.security.blocked_reason;
        return r ? (
          <Tag color="red" style={{ borderRadius: 10 }}>
            {r}
          </Tag>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 170,
      render: (_, row) => (
        <Popconfirm
          title="Restore this account?"
          description="The user will be able to sign in again immediately."
          okText="Restore access"
          cancelText="Cancel"
          onConfirm={() => handleRestore(row._id)}
        >
          <Button
            type="primary"
            className="sl-btn-primary"
            icon={<UnlockOutlined />}
            loading={restoringId === row._id}
            style={primaryBtn}
          >
            Restore access
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "24px 28px",
        marginTop: "70px",
        marginLeft: 8,
        background: "#f5f6f8",
        minHeight: "calc(100vh - 70px)",
        width: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: BRAND,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            <LockOutlined />
          </div>
          <div>
            <h3 style={{ margin: 0, color: BRAND, fontWeight: 700 }}>
              Blocked Accounts{" "}
              <Badge
                count={data.length}
                showZero
                style={{ background: "#ff4d4f", marginLeft: 6 }}
              />
            </h3>
            <span style={{ color: "#8c8c8c", fontSize: 13 }}>
              Accounts blocked after repeated failed logins — only a super admin can restore
              access
            </span>
          </div>
        </div>
        <Button
          className="sl-btn-ghost"
          icon={<ReloadOutlined />}
          onClick={fetch}
          style={ghostBtn}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 8 }}
      >
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={data}
          columns={columns}
          size="middle"
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No blocked accounts"
              />
            ),
          }}
          pagination={{ pageSize: 25, showTotal: (t) => `${t} blocked` }}
        />
      </Card>
    </div>
  );
};

export default LockedAccountsTable;
